<script setup lang="ts">
import type { BlobObject } from '../../../types/gallery'
import type { GalleryMeta } from '../../../types/gallery-meta'
import type { TopicPageData } from '../../../types/topic-meta'

const route = useRoute()
const { images, getImages } = useFile()
const toast = useToast()

const pathSegments = computed(() => {
  const value = route.params.path
  return (Array.isArray(value) ? value : [value])
    .flatMap(segment => typeof segment === 'string' ? segment.split('/') : [])
    .map(segment => segment.trim())
    .filter(Boolean)
})

const galleriesIndex = computed(() => pathSegments.value.indexOf('galleries'))
const galleryName = computed(() => {
  const index = galleriesIndex.value
  return index >= 0 ? (pathSegments.value[index + 1] || '') : ''
})
const topicPath = computed(() => {
  const index = galleriesIndex.value
  return index >= 0 ? pathSegments.value.slice(0, index) : pathSegments.value
})

const [{ data: topicData, error: topicError, refresh: refreshTopicData }, { data: gallery, error: galleryError, refresh: refreshGallery }] = await Promise.all([
  useFetch<TopicPageData>(() => `/api/topics/${topicPath.value.join('/')}`),
  useFetch<GalleryMeta>(() => `/api/galleries/${galleryName.value}`)
])

function sortImagesByGalleryOrder(files: BlobObject[], galleryEntryOrder: string[] = []) {
  if (!galleryEntryOrder.length) {
    return files
  }

  const imageOrder = galleryEntryOrder
    .filter(entryId => entryId.startsWith('image:'))
    .map(entryId => entryId.replace(/^image:/, ''))

  if (!imageOrder.length) {
    return files
  }

  const byPathname = new Map(files.map(file => [file.pathname, file]))
  const ordered: BlobObject[] = []

  for (const pathname of imageOrder) {
    const image = byPathname.get(pathname)
    if (image) {
      ordered.push(image)
      byPathname.delete(pathname)
    }
  }

  for (const file of files) {
    if (byPathname.has(file.pathname)) {
      ordered.push(file)
      byPathname.delete(file.pathname)
    }
  }

  return ordered
}

const isUnlockModalOpen = ref(false)
const unlockPassword = ref('')
const unlockingGallery = ref(false)

async function unlockGallery() {
  if (!unlockPassword.value.trim() || unlockingGallery.value) {
    return
  }

  unlockingGallery.value = true

  await $fetch('/api/galleries/unlock', {
    method: 'POST',
    body: {
      name: galleryName.value,
      password: unlockPassword.value
    }
  }).then(async () => {
    await Promise.all([
      refreshTopicData(),
      refreshGallery()
    ])

    if (gallery.value) {
      await getImages(galleryName.value)

      if (images.value) {
        images.value = sortImagesByGalleryOrder(images.value, gallery.value.galleryEntryOrder || [])
      }
    }

    isUnlockModalOpen.value = false
    unlockPassword.value = ''
  }).catch((err) => {
    toast.add({
      color: 'red',
      title: 'Invalid gallery password',
      description: err.data?.message || err.message
    })
  }).finally(() => {
    unlockingGallery.value = false
  })
}

if (galleryError.value?.statusCode === 401) {
  isUnlockModalOpen.value = true
}

if (topicError.value || galleryError.value || !topicData.value || !gallery.value || gallery.value.topicId !== topicData.value.topic.id) {
  if (galleryError.value?.statusCode === 401) {
    // keep page alive to show password prompt
  }
  else {
    throw createError({
      statusCode: 404,
      statusMessage: `No such gallery at this topic path: ${galleryName.value}.`
    })
  }
}

if (gallery.value) {
  await getImages(galleryName.value)

  if (images.value) {
    images.value = sortImagesByGalleryOrder(images.value, gallery.value.galleryEntryOrder || [])
  }
}
</script>

<template>
  <div>
    <UModal
      v-model:open="isUnlockModalOpen"
      title="Gallery password required"
      :dismissible="false"
      :close="false"
    >
      <template #body>
        <form
          class="flex flex-col gap-4"
          @submit.prevent="unlockGallery"
        >
          <UInput
            v-model="unlockPassword"
            type="password"
            placeholder="Enter gallery password"
            autocomplete="current-password"
            required
          />
          <div class="flex justify-end">
            <UButton
              type="submit"
              :loading="unlockingGallery"
              label="Unlock"
              color="primary"
            />
          </div>
        </form>
      </template>
    </UModal>

    <ImageDetail v-if="gallery" />
  </div>
</template>
